import axios from "axios";

import { apiConfig } from "../config/authConfig";
import { CredentialManager } from "../classes/credentialManager";

import { IAddress } from "../types/base";
import { ITransporter, ITransporterContact, ITransporterDetails, ITransporterExportRequest, ITransporterLight, ITransporterVendor } from "../types/transporter";
import { IFuzzySearchRequest } from "../types/system";

import { DocumentService } from "./documentService";

export namespace TransporterService {
	const credentialManager = CredentialManager.getInstance();

	/**********
	 * Convert one api response object to one Transporter.
	 * @param data Transporter response data from an API.
	 **********/
	export function mapTransporter(data: any) {
		let transporter: ITransporter = {
			contact: data.contact,
			details: data.details,
			documents: DocumentService.mapDocuments(data.documents),
			id: data.id.toString(),
			mailingAddress: data.mailingAddress,
			physicalAddress: data.physicalAddress,
			vendors: data.vendors
		}

		return transporter;
	}

	/**********
	 * Convert an array of api response objects to an array of Transporters.
	 * @param data Transporter data from an API response.
	 **********/
	export function mapTransporters(data: any) {
		let transporters: ITransporter[] = [];

		if (Array.isArray(data) && data.length > 0) {
			data.map((item: any) => {
				transporters.push(mapTransporter(item));
			})
		}

		return transporters;
	}

	/**********
	 * Convert one api response object to one Transporter.
	 * @param data Transporter response data from an API.
	 **********/
	export function mapTransporterLight(data: any) {
		let transporter: ITransporterLight = {
			id: data.id,
			name: data.name,
			city: data.city,
			state: data.state,
			status: data.status,
		}

		return transporter;
	}

	/**********
	 * Convert an array of api response objects to an array of Transporters.
	 * @param data Transporter data from an API response.
	 **********/
	export function mapTransporterLightList(data: any) {
		let transporters: ITransporterLight[] = [];

		if (Array.isArray(data) && data.length > 0) {
			data.map((item: any) => {
				transporters.push(mapTransporterLight(item));
			})
		}

		return transporters;
	}

	/**********
	 * Gets all transporters.
	 **********/
	export async function getTransporters() {
		const apiResponse = axios.get(
			apiConfig.default + "/transporter/all",
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Gets all transporters associated with a single vendor.
	 * @param vendorId The id of the vendor.
	 **********/
	export async function getAllByVendor(vendorId: number) {
		const apiResponse = axios.get(
			apiConfig.default + "/transporter" + (vendorId >= 0 ? "/vendor/" + vendorId : "/all"),
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Gets a transporter.
	 * @param transporterId The id of the transporter.
	 **********/
	export async function getTransporter(transporterId: string) {
		const employeeResponse = axios.get(
			apiConfig.default + "/transporter/" + transporterId,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return employeeResponse;
	}

	/**********
	 * Add a new transporter starting with details.
	 * @param details The transporter's details.
	 **********/
	export async function addTransporter(details: ITransporterDetails) {
		const apiResponse = axios.put(
			apiConfig.default + "/transporter",
			details,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Update a transporter's details.
	 * @param transporterId The transporter's id.
	 * @param details The transporter's details.
	 **********/
	export async function setDetails(transporterId: string, details: ITransporterDetails) {
		const apiResponse = axios.put(
			apiConfig.default + "/transporter/" + transporterId + "/details",
			details,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Update a transporter's addresses.
	 * @param transporterId The transporter's id.
	 * @param physicalAddress The transporter's physical address.
	 * @param mailingAddress The transporter's mailing address.
	 **********/
	export async function setAddresses(transporterId: string, physicalAddress: IAddress, mailingAddress: IAddress) {
		let payload = {
			"physicalAddress": physicalAddress,
			"mailingAddress": mailingAddress
		}

		const apiResponse = axios.put(
			apiConfig.default + "/transporter/" + transporterId + "/addresses",
			payload,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Update a transporter's contact.
	 * @param transporterId The transporter's id.
	 * @param contact The transporter's contact.
	 **********/
	export async function setContact(transporterId: string, contact: ITransporterContact) {
		const apiResponse = axios.put(
			apiConfig.default + "/transporter/" + transporterId + "/contact",
			contact,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Add a transporter to vendor association.
	 * @param transporterId The transporter's id.
	 * @param vendor The vendor's id and distance.
	 **********/
	export async function addVendor(transporterId: string, vendor: ITransporterVendor) {
		const apiResponse = axios.put(
			apiConfig.default + "/transporter/" + transporterId + "/vendor",
			vendor,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Remove a transporter to vendor association.
	 * @param transporterId The transporter's id.
	 * @param vendorId The vendor's id.
	 **********/
	export async function removeVendor(transporterId: string, vendorId: string) {
		const apiResponse = axios.delete(
			apiConfig.default + "/transporter/" + transporterId + "/vendor/" + vendorId,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Search for duplicate existing transporter names using a fuzzy compare method.
	 **********/
	export async function fuzzySearchForDuplicateTransporterNames(request: IFuzzySearchRequest) {
		const apiResponse = axios.post(
			apiConfig.default + "/transporter/fuzzy/transportername",
			request,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Exports the entire filtered transporter list to an Excel document.
	 * @param request The export request parameters containing filter and sort options.
	 **********/
	export async function exportTransporterListToExcel(request: ITransporterExportRequest) {
		const apiResponse = axios.post(
			apiConfig.default + `/transporter/export`,
			request,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}
}

